import { BRAND_LOGO_ART, type BrandLogoVariant } from "./art"
import { cn } from "@/lib/utils"

export type { BrandLogoVariant }

/** Minimum sizes from BRAND.md. Below the lockup minimum, use the symbol alone. */
export const BRAND_LOGO_MIN_SIZE = {
  /** Symbol alone: favicon, app icon, avatar. */
  isotipo: 16,
  /** Primary lockup, measured on its width. */
  lockup: 88,
} as const

type BrandLogoProps = {
  variant?: BrandLogoVariant
  /**
   * Accessible name. Pass `null` when the logo sits beside the visible word
   * "Menny" or inside an already-labelled link, so a screen reader does not
   * announce the brand twice.
   */
  title?: string | null
  /**
   * What fills the booth. `"papel"` paints it, which is what the mark is
   * actually drawn as; `"knockout"` leaves it as a hole so whatever is behind
   * shows through. Reach for `"knockout"` only when the mark must be genuinely
   * one colour — a thermal receipt, a single-colour print run, a stencil.
   */
  booth?: "papel" | "knockout"
  className?: string
}

/**
 * The Menny mark — "The Booth": two angled forms around a shared centre, reading
 * as an `M` in the lockup and as a booth in profile on the second look.
 *
 * COLOUR IS THE CALLER'S. Every path fills with `currentColor`, so the mark takes
 * the text colour of wherever it is placed: `text-brand-solid` for the identity moment,
 * `text-foreground` to sit quietly in an operational header, `text-background` to
 * knock out on a Comal panel. That is deliberate — BRAND.md requires the mark to
 * survive in one colour, and hardcoding Chile Ancho here would make the knockout
 * case impossible. It also means an unstyled instance inherits body text colour
 * rather than rendering invisible.
 *
 * SIZE IS ALSO THE CALLER'S, through `className`. The SVG is `h-8 w-auto` by
 * default and scales on height, since every lockup in the product sits in a bar
 * of a known height. Do not go below `BRAND_LOGO_MIN_SIZE`: at 16px the centre
 * table of the booth closes up and the mark reads as two ticks, which is why
 * BRAND.md still lists a small-size optical variant as an open deliverable.
 */
export function BrandLogo({
  variant = "lockup",
  title = "Menny",
  booth = "papel",
  className,
}: BrandLogoProps) {
  const art = BRAND_LOGO_ART[variant]

  return (
    <svg
      viewBox={art.viewBox}
      className={cn("h-8 w-auto shrink-0 fill-current", className)}
      role={title ? "img" : undefined}
      aria-label={title ?? undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      data-testid={`brand-logo-${variant}`}
    >
      {art.backing && booth === "papel" ? (
        <path
          d={art.backing}
          className="fill-brand-surface-foreground"
          data-testid="brand-logo-booth"
        />
      ) : null}
      {art.paths.map((d) => (
        <path key={d.slice(0, 32)} d={d} fillRule="evenodd" />
      ))}
    </svg>
  )
}

const LOCKUP_SIZES = {
  md: "h-6",
  lg: "h-7",
} as const

type BrandLockupProps = {
  size?: keyof typeof LOCKUP_SIZES
  title?: string | null
  className?: string
}

/**
 * The lockup as it was drawn: symbol and wordmark, one colour, with the optical
 * alignment already in the artwork rather than re-derived from a flex gap.
 *
 * It briefly was not that. While the product's primary action was still neutral
 * this rendered the symbol in Chile Ancho beside the wordmark in ink, on the
 * argument that an all-red wordmark was more colour than the operational
 * register allowed. `--primary` is Chile Ancho on every surface now, so that
 * argument is gone and the split was left inventing a lockup the brand does not
 * have. Ship the drawn one.
 *
 * Only two sizes exist on purpose. `md` is 24px tall, which puts the lockup at
 * roughly 97px wide — just past the 88px minimum BRAND.md sets, below which it
 * says to use the symbol alone.
 */
export function BrandLockup({ size = "md", title = "Menny", className }: BrandLockupProps) {
  return (
    <BrandLogo
      variant="lockup"
      title={title}
      className={cn(LOCKUP_SIZES[size], "text-brand-solid", className)}
    />
  )
}
